/** Site-local date and time helpers (no dependency: Intl only). Times are stored in UTC and shown in the site's zone. */

function parts(ms: number, timeZone: string): Record<string, number> {
  const f = new Intl.DateTimeFormat('en-GB', { timeZone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const out: Record<string, number> = {};
  for (const p of f.formatToParts(new Date(ms))) if (p.type !== 'literal') out[p.type] = Number(p.value);
  return out;
}

/** Offset of the zone from UTC at an instant, in ms. */
function offsetAt(ms: number, timeZone: string): number {
  const p = parts(ms, timeZone);
  return Date.UTC(p['year'] ?? 1970, (p['month'] ?? 1) - 1, p['day'] ?? 1, p['hour'] ?? 0, p['minute'] ?? 0, p['second'] ?? 0) - Math.floor(ms / 1000) * 1000;
}

/** "2026-11-03" + "09:30" in a zone -> UTC ISO string. */
export function localToUtcIso(date: string, time: string, timeZone: string): string {
  const [y, mo, d] = date.split('-').map(Number);
  const [h, mi] = time.split(':').map(Number);
  const wall = Date.UTC(y ?? 1970, (mo ?? 1) - 1, d ?? 1, h ?? 0, mi ?? 0);
  let guess = wall - offsetAt(wall, timeZone);
  guess = wall - offsetAt(guess, timeZone);
  return new Date(guess).toISOString();
}

/** UTC ISO string -> the zone's { date: 'yyyy-mm-dd', time: 'HH:MM' }. */
export function utcToLocal(iso: string, timeZone: string): { date: string; time: string } {
  const p = parts(new Date(iso).getTime(), timeZone);
  const two = (n: number | undefined) => String(n ?? 0).padStart(2, '0');
  return { date: `${p['year']}-${two(p['month'])}-${two(p['day'])}`, time: `${two(p['hour'])}:${two(p['minute'])}` };
}

/** Today's date in a zone, 'yyyy-mm-dd'. */
export function todayIn(timeZone: string, now: Date = new Date()): string {
  return utcToLocal(now.toISOString(), timeZone).date;
}

/** Adds whole days to a 'yyyy-mm-dd' date (calendar arithmetic, no zone involved). */
export function addDays(date: string, days: number): string {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, (d ?? 1) + days)).toISOString().slice(0, 10);
}

/** Whole days from one 'yyyy-mm-dd' to another (to - from). */
export function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}

/** Weekday and date for a day heading, e.g. "Tue 3 Nov". The date is a calendar day, so no zone shifts it. */
export function dayLabel(date: string): string {
  return new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' }).format(new Date(`${date}T00:00:00Z`));
}

/** Time of day in a zone, "HH:MM". */
export function clockIn(iso: string, timeZone: string): string {
  return utcToLocal(iso, timeZone).time;
}
